/**
 * Crisp, infinitely-scalable Avida house-and-leaf mark.
 * Recreated as inline SVG so it renders sharp at any size and supports
 * per-element animation (draw-in house, breathing gold leaf, swaying leaves).
 *
 * Props:
 *   size       — pixel width/height of the square mark (default 80)
 *   animate    — enable entrance + idle animations (default true)
 *   navy       — house stroke colour (default #0A2B5C)
 *   gold       — centre leaf colour (default #FFDA00)
 *   blueDark   — outer leaf dark shade (default #0A4DA8)
 *   blueLight  — outer leaf light shade (default #3B8DCC)
 */
export default function AvidaLogo({
  size = 80,
  animate = true,
  navy = "#0A2B5C",
  gold = "#FFDA00",
  blueDark = "#0A4DA8",
  blueLight = "#3B8DCC",
  houseOnly = false,
  houseStrokeWidth,
  className = "",
}) {
  // In house-only mode render just the house outline, a touch bolder.
  const strokeW = houseStrokeWidth || (houseOnly ? 16 : 10);
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Avida"
      role="img"
      data-testid="avida-logo-svg"
    >
      <defs>
        <linearGradient id="av-leaf-left" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={blueLight} />
          <stop offset="100%" stopColor={blueDark} />
        </linearGradient>
        <linearGradient id="av-leaf-right" x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={blueLight} />
          <stop offset="100%" stopColor={blueDark} />
        </linearGradient>
        <linearGradient id="av-leaf-center" x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="#FFE94A" />
          <stop offset="100%" stopColor={gold} />
        </linearGradient>
      </defs>

      {/* House outline with chimney */}
      <path
        d="M28 92 L100 34 L146 71 L146 56 L160 56 L160 82 L172 92 L172 170 L28 170 Z"
        fill="none"
        stroke={navy}
        strokeWidth={strokeW}
        strokeLinejoin="round"
        strokeLinecap="round"
        className={animate ? "av-house" : ""}
        pathLength="1"
      />

      {!houseOnly && (
        <>
          {/* Left blue leaf */}
          <path
            d="M100 150 Q 60 138 42 108 Q 62 110 82 124 Q 96 134 100 150 Z"
            fill="url(#av-leaf-left)"
            className={animate ? "av-leaf-left" : ""}
            style={{ transformOrigin: "100px 150px" }}
          />

          {/* Right blue leaf */}
          <path
            d="M100 150 Q 140 138 158 108 Q 138 110 118 124 Q 104 134 100 150 Z"
            fill="url(#av-leaf-right)"
            className={animate ? "av-leaf-right" : ""}
            style={{ transformOrigin: "100px 150px" }}
          />

          {/* Center gold leaf (breathing) */}
          <path
            d="M100 66
               Q 84 96 90 128
               Q 95 140 100 150
               Q 105 140 110 128
               Q 116 96 100 66 Z"
            fill="url(#av-leaf-center)"
            className={animate ? "av-leaf-center" : ""}
            style={{ transformOrigin: "100px 108px" }}
          />

          {/* Stem centre dot */}
          <circle
            cx="100"
            cy="158"
            r="5"
            fill={gold}
            className={animate ? "av-dot" : ""}
            style={{ transformOrigin: "100px 158px" }}
          />
        </>
      )}

      <style>{`
        @keyframes av-draw {
          from { stroke-dashoffset: 1; }
          to   { stroke-dashoffset: 0; }
        }
        @keyframes av-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
        @keyframes av-sway-left {
          0%, 100% { transform: rotate(0deg); }
          50%      { transform: rotate(-3deg); }
        }
        @keyframes av-sway-right {
          0%, 100% { transform: rotate(0deg); }
          50%      { transform: rotate(3deg); }
        }
        @keyframes av-fade-up {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes av-pulse-dot {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.25); opacity: 0.85; }
        }
        .av-house {
          stroke-dasharray: 1;
          stroke-dashoffset: 1;
          animation: av-draw 1.4s ease-out 0.1s forwards;
        }
        .av-leaf-left {
          opacity: 0;
          animation:
            av-fade-up 0.6s ease-out 1.0s forwards,
            av-sway-left 6s ease-in-out 1.6s infinite;
        }
        .av-leaf-right {
          opacity: 0;
          animation:
            av-fade-up 0.6s ease-out 1.1s forwards,
            av-sway-right 6s ease-in-out 1.7s infinite;
        }
        .av-leaf-center {
          opacity: 0;
          animation:
            av-fade-up 0.6s ease-out 1.25s forwards,
            av-breathe 4.5s ease-in-out 2s infinite;
        }
        .av-dot {
          opacity: 0;
          animation:
            av-fade-up 0.4s ease-out 1.45s forwards,
            av-pulse-dot 2.2s ease-in-out 2.1s infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .av-house, .av-leaf-left, .av-leaf-right, .av-leaf-center, .av-dot {
            animation: none !important;
            opacity: 1 !important;
            stroke-dashoffset: 0 !important;
          }
        }
      `}</style>
    </svg>
  );
}
